/* CasesPipeline — Phase 1 of the Aligner Cases redesign (iter98.23).
 *
 * Replaces the legacy 2-tab `Cases.jsx` ↔ `AlignerInsights.jsx` view with a
 * 3-view toggle (Pipeline · Compliance · Schedule). Phase 1 ships only the
 * Pipeline view fully — Compliance is mounted via the existing
 * <AlignerInsights /> component as a transitional bridge, and Schedule is
 * a "Coming soon" placeholder. Phases 2 + 3 will rebuild each from the
 * provided HTML mockup once the doctor approves the direction.
 *
 * Data sources:
 *   • GET /api/cases                       — full case list (pipeline buckets)
 *   • GET /api/doctor/insights/compliance  — wear / engagement enrichment for
 *                                            in-treatment cases (table cols
 *                                            "Wear" + "Time in stage")
 *
 * The two responses are merged client-side on case_id so the Pipeline table
 * shows compliance signals where they exist and gracefully falls back to
 * "—" for pre-treatment cases.
 *
 * Apollo cyan-teal palette throughout (no mockup-emerald). Each stage card
 * carries a top accent strip in its phase colour for at-a-glance grouping.
 *
 * Revert: the legacy `Cases.jsx` is untouched in the codebase. To roll
 * back, point the `/doctor/cases` route to `DoctorCases` again — or use
 * Emergent's "Rollback" feature from any pre-iter98.23 checkpoint.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import {
    Plus, MagnifyingGlass, Hexagon, ChartLine, CalendarBlank, Download,
    Bell, ArrowsClockwise, ListBullets, Warning, Clock, TrendUp,
    Eye, CalendarPlus, ArrowSquareOut, X, ArrowsDownUp, Calendar,
    Funnel, FlowArrow, Notebook,
    // iter98.83 — extra icons for the redesigned case preview drawer.
    Tooth, Heartbeat, Path, CheckCircle, Circle as CircleIcon, MapPin,
    IdentificationCard,
    // iter98.99dh — Compliance & Monitoring 2-state visual system.
    // ShieldCheck = Invisique-monitored; Stethoscope = Self-monitored.
    ShieldCheck, Stethoscope,
    // iter101.77 — Row-level Action column icons.
    PencilLine, CaretRight, Sparkle,
} from "@phosphor-icons/react";
import CasesPipelineCompliance from "@/pages/doctor/CasesPipelineCompliance";
import CasesPipelineSchedule from "@/pages/doctor/CasesPipelineSchedule";
import { computeResumeStep, computeDraftProgress } from "@/components/case/DraftCasesCard";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { statusLabel as sharedStatusLabel, statusTone as sharedStatusTone } from "@/lib/caseStatus";


/* Helper: shorten a long UUID-ish patient_id into a 12-char display. */
const humanPatientId = (id) => (id ? String(id).slice(0, 12) : "");


/* Deterministic tint + initials helpers — mirror the same logic used in
 * PatientChat.jsx so the same patient gets the same avatar colour across
 * portal surfaces. Inlined here to keep the file self-contained. */
const AVATAR_TINTS = [
    { bg: "bg-primary/15", border: "border-primary/30", text: "text-primary" },
    { bg: "bg-indigo-50",  border: "border-indigo-200", text: "text-indigo-700" },
    { bg: "bg-amber-50",   border: "border-amber-200", text: "text-amber-800" },
    { bg: "bg-rose-50",    border: "border-rose-200", text: "text-rose-700" },
    { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
    { bg: "bg-sky-50",     border: "border-sky-200", text: "text-sky-700" },
];
function avatarTintFor(name) {
    const s = String(name || "?");
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
    return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
}
const initialsOf = (name) =>
    String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";


/* MonitoringMarker — tiny shield / stethoscope chip surfaced next to the
   patient name on the Pipeline table row AND inside the Case Detail
   header strip. Three-state, locked-at-submission semantic:

      • coe_support_compliance === true  → Invisique-monitored (ShieldCheck, primary)
      • coe_support_compliance === false → Self-monitored      (Stethoscope, slate)
      • coe_support_compliance == null AND status is active → red pulsing dot
        (iter100.73 — nudges the doctor to click into the case and choose;
        signals a portfolio-level gap when scanned across all rows)
      • coe_support_compliance == null AND status ∈ {draft, cancelled, completed}
        → render nothing (choice not yet meaningful / no longer meaningful)

   iter98.99dh — Implements "Option G" of the doctor-facing 2-state
   visual system. The icon doubles as a one-tap shortcut: clicking it
   filters the pipeline table to "Invisique-monitored" or "Self-monitored"
   so the doctor can pivot from "what is this case?" to "show me my whole
   monitoring cohort" without leaving the row. */
function MonitoringMarker({ mode, caseId, size = "row", status }) {
    // iter100.76 — Two-state semantic. Invisique-managed (mode === true)
    // is the only "committed" state; anything else (null | false) means
    // the doctor is self-managing (default). For active cases that
    // could still be upgraded (i.e., pre-`in_treatment`), we surface a
    // red pulsing dot to nudge the doctor. Terminal / in-treatment /
    // draft cases render nothing.
    if (mode !== true) {
        const NUDGE_STATUSES_EXCLUDE = ["draft", "cancelled", "completed", "in_treatment", "treatment_started", "refinement_requested"];
        const shouldNudge = status && !NUDGE_STATUSES_EXCLUDE.includes(status);
        if (!shouldNudge) return null;
        const sizeCls = size === "header" ? "w-6 h-6" : "w-[18px] h-[18px]";
        return (
            <span
                role="img"
                aria-label="Compliance is self-managed — click to upgrade to Invisique-managed"
                title="Compliance is self-managed — click to upgrade to Invisique-managed"
                className={`inline-flex items-center justify-center rounded-full ring-1 shrink-0 bg-rose-50 ring-rose-300 animate-compliance-pulse ${sizeCls}`}
                data-testid={`monitoring-marker-${caseId || ""}-unset`}
            >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" aria-hidden />
            </span>
        );
    }
    const isInvisique = mode === true;
    const Icon = isInvisique ? ShieldCheck : Stethoscope;
    const cls = isInvisique
        ? "text-primary bg-primary/10 ring-primary/25 hover:bg-primary/15"
        : "text-slate-600 bg-slate-100 ring-slate-300 hover:bg-slate-200";
    const sizeCls = size === "header"
        ? "w-6 h-6"
        : "w-[18px] h-[18px]";
    const iconSize = size === "header" ? 13 : 11;
    const label = isInvisique
        ? "Invisique™ compliance & monitoring · active"
        : "Self-monitored compliance · doctor managed";
    return (
        <span
            role="img"
            aria-label={label}
            title={label}
            className={`inline-flex items-center justify-center rounded-full ring-1 transition-colors shrink-0 ${cls} ${sizeCls}`}
            data-testid={`monitoring-marker-${caseId || ""}-${isInvisique ? "invisique" : "self"}`}
        >
            <Icon size={iconSize} weight={isInvisique ? "fill" : "duotone"} />
        </span>
    );
}



/* ──────────────────────────────────────────────────────────────────────
   STAGE MAPPING — collapses our 13 backend statuses into the 7 doctor-
   facing stages from the HTML mockup. Each new stage carries the colour
   that drives the phase card top-rail and the stage pill in the table.
   ────────────────────────────────────────────────────────────────────── */
const PIPELINE_STAGES = [
    {
        // iter100.273 — Split drafts out of "Records Due". A draft case is
        // the doctor's unfinished submission (their own to-do), not a
        // "records still being collected from patient" state. Previously
        // drafts leaked into `records-due`, which then leaked into the
        // "Need Action · Review now" KPI — showing an alarming red pill
        // for what is really just a saved-but-unsubmitted draft.
        key: "draft",
        label: "Drafts",
        sub: "your work in progress",
        tone: "slate",
        phase: "intake",
        statuses: ["draft"],
    },
    {
        key: "records-due",
        label: "Records Due",
        sub: "awaiting scans",
        tone: "rose",
        phase: "intake",
        statuses: ["scan_visit_scheduled", "scan_visit_completed"],
    },
    {
        key: "in-planning",
        label: "In Planning",
        sub: "with lab / planner",
        tone: "indigo",
        phase: "intake",
        statuses: ["submitted", "assigned_to_planner", "in_planning", "submitted_to_qa", "in_qa_review", "plan_authoring", "pending_admin_review"],
    },
    {
        key: "doctor-review",
        label: "Doctor Review",
        sub: "awaiting your approval",
        tone: "amber",
        phase: "intake",
        statuses: ["sent_for_approval", "ready_for_review", "revision_requested", "refinement_requested", "info_requested"],
    },
    {
        // iter101.6 — Split out from Active Treatment. Post-approval,
        // pre-fitment cases now live in their own "In Production" pill
        // so the status label ("Approved · Preparing Production" /
        // "In Production") and the bucket label finally agree. Before
        // this split every approved/in-manufacturing/shipped case was
        // being tallied under "Active Treatment · wearing aligners",
        // which was factually wrong (the patient wasn't wearing
        // anything yet) and confused doctors on the dashboard.
        key: "in-production",
        label: "In Production",
        sub: "approved · being made",
        tone: "primary-mid",
        phase: "active",
        statuses: [
            "approved", "approved_pending_mfg", "awaiting_production_assignment",
            "assigned_to_production", "submitted_to_production", "in_production",
            "shipped",
        ],
    },
    {
        key: "active-treatment",
        label: "Active Treatment",
        sub: "wearing aligners",
        tone: "primary",
        phase: "active",
        // iter101.6 — Slimmed down to only true active-treatment
        // statuses. Post-approval / production statuses now live under
        // the new "In Production" pill above.
        statuses: [
            "in_treatment", "treatment_started", "refinement_requested",
        ],
    },
    {
        key: "monitoring",
        label: "Monitoring",
        sub: "compliance review",
        tone: "primary-mid",
        phase: "active",
        // Soft-derived from in-treatment cases with at-risk / needs-attention
        // compliance buckets — set by enrichCases() after merging compliance
        // data, not directly by case status.
        statuses: [],
    },
    {
        key: "retention",
        label: "Retention",
        sub: "in retainer phase",
        tone: "primary-soft",
        phase: "completion",
        statuses: [],
    },
    {
        key: "complete",
        label: "Complete",
        sub: "treatment done",
        tone: "neutral",
        phase: "completion",
        // iter100.77 — Only `completed` counts as Complete. Prior code
        // included `cancelled` here — a cancelled case is a TERMINATED
        // case, not a successful completion. Bucketing them together
        // inflated "Complete: N" on doctors whose only case was
        // cancelled (reported directly by the user on Dr. Karuna's
        // dashboard: 1 total, 1 complete, when the only case was
        // cancelled). Cancelled cases are excluded from the pipeline
        // entirely via the `enriched` filter — they represent no
        // outstanding work and no successful outcome.
        statuses: ["completed"],
    },
    {
        // iter100.78 — Dedicated pill for cancelled (terminated) cases so
        // the doctor keeps visibility on them without contaminating any
        // "active pipeline" count. Statuses stays empty because the
        // count is derived from a parallel `cancelledCases` list (see
        // CasesPipeline body). Rose-tinted rail signals terminal state.
        key: "cancelled",
        label: "Cancelled",
        sub: "terminated cases",
        tone: "rose",
        phase: "completion",
        statuses: [],
    },
];

const PHASE_META = {
    intake: { label: "Intake", icon: "📥", colour: "text-indigo-600" },
    active: { label: "Active", icon: "⚙️", colour: "text-primary" },
    completion: { label: "Completion", icon: "🏁", colour: "text-muted-foreground" },
};

const TONE_STYLES = {
    rose: { topRail: "bg-rose-500", text: "text-rose-600", chip: "bg-rose-50 text-rose-700 border-rose-200", barFill: "bg-rose-500" },
    indigo: { topRail: "bg-indigo-500", text: "text-indigo-700", chip: "bg-indigo-50 text-indigo-700 border-indigo-200", barFill: "bg-indigo-500" },
    amber: { topRail: "bg-amber-500", text: "text-amber-700", chip: "bg-amber-50 text-amber-700 border-amber-200", barFill: "bg-amber-500" },
    primary: { topRail: "bg-primary", text: "text-primary", chip: "bg-primary/10 text-primary border-primary/30", barFill: "bg-primary" },
    "primary-mid": { topRail: "bg-primary/70", text: "text-primary/80", chip: "bg-primary/5 text-primary/80 border-primary/20", barFill: "bg-primary/70" },
    "primary-soft": { topRail: "bg-primary/40", text: "text-primary/60", chip: "bg-primary/5 text-primary/60 border-primary/15", barFill: "bg-primary/40" },
    neutral: { topRail: "bg-muted-foreground/40", text: "text-muted-foreground", chip: "bg-secondary text-muted-foreground border-border", barFill: "bg-muted-foreground/40" },
    slate: { topRail: "bg-slate-400", text: "text-slate-600", chip: "bg-slate-50 text-slate-700 border-slate-200", barFill: "bg-slate-400" },
};

const PHASES_ORDER = ["intake", "active", "completion"];


/* Lookup helper — pick the stage for a single case. Monitoring +
 * Retention are derived after the compliance merge (see enrichCases).
 *
 * iter101.5 — Fallback used to be `"complete"` which was a bug: any
 * status not enumerated in PIPELINE_STAGES got miscounted as a
 * completed case (that's how `approved_pending_mfg` ended up
 * inflating the "Complete: 1" pill in production). Fallback is now
 * `"active-treatment"` — the safer bucket for "we know it's live but
 * we don't recognise this exact sub-status", and it also matches how
 * `enrichCases()` post-processes derived buckets. */
function stageKeyForCase(c) {
    for (const s of PIPELINE_STAGES) {
        if (s.statuses.includes(c.status)) return s.key;
    }
    return "active-treatment";
}

/* iter98.99ap — Doctor-facing status labels for the "Status" column in
   the Cases table. iter101.0 — Labels are now sourced from the central
   `/lib/caseStatus.js` STATUS_CONFIG so the Dashboard, Aligner Cases
   list, Case Detail and Patients list all speak the same language.
   Only the tone mapping stays local because this file uses a smaller
   4-token palette (neutral / primary / amber / rose) rather than the
   6-token palette in caseStatus.js. */
const SHARED_TONE_TO_LOCAL = {
    slate:   "neutral",
    sky:     "primary",
    violet:  "primary",
    emerald: "primary",
    amber:   "amber",
    rose:    "rose",
};

/* iter98.99ap — Doctor-facing "Action needed" copy per case status.
   `pending` flag drives the row pill colour: amber = the doctor has
   work to do · slate = waiting on Invisique · emerald = case is closed
   or in steady-state. Short copy so the column stays scannable at
   common laptop widths. */
const ACTION_NEEDED_MAP = {
    draft:                   { text: "Resume drafting",              tone: "amber" },
    scan_visit_scheduled:    { text: "Awaiting technician visit",    tone: "slate" },
    scan_visit_completed:    { text: "Resume case wizard",           tone: "amber" },
    submitted:               { text: "Awaiting Invisique™",           tone: "slate" },
    in_planning:             { text: "Planner authoring",            tone: "slate" },
    plan_authoring:          { text: "Awaiting Invisique™",           tone: "slate" },
    submitted_to_qa:         { text: "Internal QA review",           tone: "slate" },
    pending_admin_review:    { text: "Awaiting admin review",        tone: "slate" },
    in_qa_review:            { text: "QA reviewing your plan",       tone: "slate" },
    sent_for_approval:       { text: "Review & approve plan",        tone: "amber" },
    ready_for_review:        { text: "Review & approve plan",        tone: "amber" },
    revision_requested:      { text: "Plan being reworked",          tone: "slate" },
    refinement_requested:    { text: "Refinement in progress",       tone: "slate" },
    info_requested:          { text: "Provide additional info",      tone: "amber" },
    approved:                { text: "Awaiting routing",              tone: "slate" },
    assigned_to_production:  { text: "Manufacturing queued",          tone: "slate" },
    submitted_to_production: { text: "Sent to lab",                   tone: "slate" },
    in_production:           { text: "Manufacturing in progress",     tone: "slate" },
    shipped:                 { text: "Confirm receipt at clinic",     tone: "amber" },
    in_treatment:            { text: "Schedule patient fitting",      tone: "amber" },
    treatment_started:       { text: "Monitor wear & check-ins",      tone: "slate" },
    completed:               { text: "Treatment complete",            tone: "emerald" },
    cancelled:               { text: "Case cancelled",                tone: "neutral" },
};

const ACTION_TONE_CLS = {
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    neutral: "bg-foreground/5 text-foreground/60 border-foreground/15",
};

const STATUS_TONE_CLS = {
    neutral: "bg-foreground/5 text-foreground/70 border-foreground/15",
    primary: "bg-primary/10 text-primary border-primary/25",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
};

function actionNeededFor(c) {
    return ACTION_NEEDED_MAP[c.status] || { text: "—", tone: "neutral" };
}

function statusLabelFor(c) {
    const label = sharedStatusLabel(c.status, "doctor");
    const sharedTone = sharedStatusTone(c.status);
    const tone = SHARED_TONE_TO_LOCAL[sharedTone] || "neutral";
    return { label: label || (c.status || "—"), tone };
}

function progressOfCase(c) {
    const upper = c.plan?.aligner_count_upper || 0;
    const lower = c.plan?.aligner_count_lower || 0;
    const total = Math.max(upper, lower);
    const stage = c.current_aligner_stage || 0;
    const pct = total > 0 ? Math.min(100, Math.round((stage / total) * 100)) : 0;
    return { stage, total, pct };
}

function humanPatientIdLong(patientId) {
    if (!patientId) return "—";
    return `INV-PT-${patientId.replace(/-/g, "").slice(0, 6).toUpperCase()}`;
}

function formatSubmittedOn(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return "—"; }
}

/* iter101.78 — Relative time helper for the new "Last activity" column.
 * Doctors triage on recency ("who moved today?"), so relative is more
 * useful than an ISO date. Falls back to a short absolute date when the
 * activity is > 30 days old (relative loses meaning at that point).
 * Returns { text, stale } — `stale=true` if the case hasn't budged in
 * more than 5 days, which the row uses to render the text in rose. */
function formatLastActivity(iso) {
    if (!iso) return { text: "—", stale: false };
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const secs = Math.max(0, Math.floor((now - then) / 1000));
        const mins = Math.floor(secs / 60);
        const hrs  = Math.floor(mins / 60);
        const days = Math.floor(hrs / 24);
        const stale = days >= 5;
        if (secs < 60)  return { text: "Just now",             stale: false };
        if (mins < 60)  return { text: `${mins} min ago`,      stale: false };
        if (hrs < 24)   return { text: `${hrs}h ago`,          stale: false };
        if (days < 7)   return { text: `${days}d ago`,         stale };
        if (days < 30)  return { text: `${Math.floor(days / 7)}w ago`, stale: true };
        return {
            text: new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
            stale: true,
        };
    } catch {
        return { text: "—", stale: false };
    }
}

/* iter101.87 — Compact single-line summary of a scan visit's date +
 * time + assigned technician. Renders as the sub-line under the ghost
 * "Awaiting scan visit" / "Scan visit — completed" row in the row-level
 * progress tooltip. Handles missing bits gracefully — a visit with a
 * date but no technician still reads cleanly. */
function formatVisitLine(date, time, techName) {
    let dateStr = "";
    if (date) {
        try {
            const d = new Date(`${date}T${time || "00:00"}:00`);
            dateStr = d.toLocaleDateString("en-IN", {
                day: "numeric", month: "short", year: "numeric",
            });
        } catch { dateStr = date; }
    }
    const pieces = [];
    if (dateStr) pieces.push(dateStr);
    if (time)    pieces.push(time);
    if (techName) pieces.push(`w/ ${techName}`);
    return pieces.length ? pieces.join(" · ") : "Visit details TBC";
}


/* iter101.78 — Split a canonical doctor status label into headline +
 * detail on the " · " separator so the table cell can render a compact
 * coloured pill on line 1 and a muted secondary phrase on line 2. This
 * prevents long labels like "Approved · Preparing Production" from
 * breaking the row layout. Labels without a "·" render as pill-only. */
function splitStatusLabel(label) {
    if (!label) return { headline: "—", detail: "" };
    const parts = String(label).split(" · ");
    if (parts.length >= 2) {
        return {
            headline: parts[0].trim(),
            detail:   parts.slice(1).join(" · ").trim(),
        };
    }
    return { headline: label.trim(), detail: "" };
}


/* ──────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ────────────────────────────────────────────────────────────────────── */
export default function CasesPipeline() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [view, setView] = useState(() => {
        const v = searchParams.get("view");
        return ["pipeline", "compliance", "schedule"].includes(v) ? v : "pipeline";
    });
    const [cases, setCases] = useState([]);
    const [insights, setInsights] = useState({ items: [], buckets: {} });
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [stageFilter, setStageFilter] = useState(() => {
        // iter98.33 — honor ?stage=<stage-key> so deep links from the Dashboard
        // ("View all" in production, etc.) land on the right pre-filtered view.
        // iter98.99dh — extended the allowlist to include the new monitoring
        // pseudo-stages so links like `?stage=monitored-invisique` work.
        const s = searchParams.get("stage");
        if (!s) return "all";
        const valid = new Set([
            "all", "flagged", "monitored-invisique", "monitored-self", "cancelled",
            ...PIPELINE_STAGES.map((p) => p.key),
        ]);
        return valid.has(s) ? s : "all";
    });
    /* iter98.33 — actual sort options, replacing the previously dead
       "Sort options coming soon" tooltip. Default mirrors what the
       backend already returns (newest submitted first). */
    const [sortBy, setSortBy] = useState("submitted-desc");
    /* iter98.99h — Clicking a row used to open an intermediate
       CasePreviewDrawer. Per user direction (2026-02), the popup is
       gone: drafts deep-link straight to the wizard at the last saved
       step, every other case opens the full Case Detail page. The
       drawer component below is kept in the file as dead code in case
       the experience needs to revert. */
    const openCaseRow = (c) => {
        if (!c) return;
        if (c.status === "draft") {
            const step = computeResumeStep(c);
            navigate(`/doctor/cases/new?draft=${c.id}&step=${step}`);
        } else {
            navigate(`/doctor/cases/${c.id}`);
        }
    };

    const setViewParam = (v) => {
        setView(v);
        const p = new URLSearchParams(searchParams);
        if (v === "pipeline") p.delete("view"); else p.set("view", v);
        setSearchParams(p, { replace: true });
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [casesR, insightsR] = await Promise.allSettled([
                    api.get("/cases"),
                    api.get("/doctor/insights/compliance"),
                ]);
                if (cancelled) return;
                if (casesR.status === "fulfilled") setCases(casesR.value.data || []);
                if (insightsR.status === "fulfilled") setInsights(insightsR.value.data || { items: [], buckets: {} });
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, []);

    /* iter100.78 — Cancelled cases live outside the "active pipeline"
     * dataset (they inflate Total / Complete / Monitored otherwise) but
     * the doctor still needs a dedicated count + drill-down. We keep
     * a parallel `cancelledCases` list and surface it as a stand-alone
     * "Cancelled" chip on the stage rail. Clicking the chip flips the
     * table over to the cancelled dataset without changing any KPI. */
    const cancelledCases = useMemo(() => {
        return (cases || [])
            .filter((c) => c.status === "cancelled")
            .map((c) => ({ ...c, _stageKey: "cancelled", _ins: null, _days_in_stage: null, _flagged: false }));
    }, [cases]);

    /* Merge compliance enrichment into the cases array so the Pipeline
     * table can render Wear / Time-in-stage / Risk-bucket inline. Cases
     * not in the compliance set (pre-treatment) keep their raw shape. */
    const enriched = useMemo(() => {
        const byCaseId = new Map();
        for (const it of insights.items || []) byCaseId.set(it.case_id, it);
        // iter100.77 — Cancelled cases are excluded from the entire
        // pipeline view. They're a terminal state with no outstanding
        // doctor work and no successful outcome — they belong in
        // neither the "active pipeline" nor the "Complete" bucket.
        // They still exist on the patient's Cases tab + the case
        // detail page (with a CANCELLED pill), so nothing is hidden
        // from the doctor — the pipeline just reflects reality:
        // "here is what you need to work on and here is what you
        // finished successfully".
        return (cases || [])
            .filter((c) => c.status !== "cancelled")
            .map((c) => {
            const ins = byCaseId.get(c.id);
            const stageKey = stageKeyForCase(c);
            // Promote in-treatment cases with at-risk/needs-attention buckets
            // into the "Monitoring" stage card so the doctor sees a clear
            // count of patients needing compliance attention. The raw status
            // is preserved on `c.status` for any downstream consumer.
            const monitoringFlag = stageKey === "active-treatment" && ins && (ins.bucket === "at-risk" || ins.bucket === "needs-attention");
            const days_in_stage = (() => {
                const ref = c.aligner_stage_updated_at || c.updated_at || c.created_at;
                if (!ref) return null;
                try { return Math.max(0, Math.floor((Date.now() - new Date(ref).getTime()) / 86400000)); } catch { return null; }
            })();
            return {
                ...c,
                _stageKey: monitoringFlag ? "monitoring" : stageKey,
                _ins: ins || null,
                _days_in_stage: days_in_stage,
                _flagged: ins?.bucket === "needs-attention" || (days_in_stage != null && days_in_stage > 30 && ["sent_for_approval", "ready_for_review", "in_planning"].includes(c.status)),
            };
        });
    }, [cases, insights]);

    /* Counts per stage card */
    const stageCounts = useMemo(() => {
        const out = {};
        for (const s of PIPELINE_STAGES) out[s.key] = 0;
        for (const c of enriched) out[c._stageKey] = (out[c._stageKey] || 0) + 1;
        // iter100.78 — Cancelled bucket counts from the parallel list so
        // the tally is exact even when the pipeline enrichment excludes
        // terminated cases.
        out.cancelled = cancelledCases.length;
        return out;
    }, [enriched, cancelledCases]);

    /* KPI insight bar — six metrics keyed off the enriched data so we
     * don't need a separate roundtrip. */
    const kpis = useMemo(() => {
        const total = enriched.length;
        const inTreatment = enriched.filter((c) => c._stageKey === "active-treatment" || c._stageKey === "monitoring" || c._stageKey === "in-production").length;
        // iter100.273 — Need Action = external states truly awaiting the
        // doctor. Drafts are the doctor's own unfinished work; showing
        // them under a red "Review now" pill was misleading and caused
        // false alarms on the dashboard.
        const needAction = enriched.filter((c) => c._stageKey === "doctor-review" || c._stageKey === "records-due").length;
        const flagged = enriched.filter((c) => c._flagged).length;
        const wearItems = (insights.items || []).filter((it) => it.wear_7d_avg != null);
        const avgWear = wearItems.length > 0 ? (wearItems.reduce((s, i) => s + (i.wear_7d_avg || 0), 0) / wearItems.length) : null;
        const buckets = insights.buckets || {};
        const onTrackPct = (buckets["on-track"] + buckets["at-risk"] + buckets["needs-attention"]) > 0
            ? Math.round((buckets["on-track"] / (buckets["on-track"] + buckets["at-risk"] + buckets["needs-attention"])) * 100)
            : null;
        // iter98.99dh — Compliance & Monitoring 2-state counts. Doctor opts
        // in at submission (`coe_support_compliance` true = Invisique runs
        // patient-side compliance; false = doctor self-manages). Drafts
        // (null) aren't counted in either bucket.
        const monitoredInvisique = enriched.filter((c) => c.coe_support_compliance === true).length;
        const monitoredSelf      = enriched.filter((c) => c.coe_support_compliance === false).length;
        return { total, inTreatment, needAction, flagged, avgWear, onTrackPct, monitoredInvisique, monitoredSelf };
    }, [enriched, insights]);

    /* Filtered table list — driven by search box + active stage filter */
    const visible = useMemo(() => {
        // iter100.78 — When the doctor picks the Cancelled chip, swap
        // the data source from the pipeline-enriched list to the
        // cancelledCases parallel list so the table shows terminated
        // cases (which live outside `enriched` by design).
        let list = stageFilter === "cancelled" ? cancelledCases : enriched;
        const q = query.trim().toLowerCase();
        if (q) {
            list = list.filter((c) =>
                (c.patient_name || "").toLowerCase().includes(q)
                || (c.code || "").toLowerCase().includes(q)
                || (c.case_code || "").toLowerCase().includes(q)
                || humanPatientId(c.patient_id).toLowerCase().includes(q),
            );
        }
        if (stageFilter === "flagged") list = list.filter((c) => c._flagged);
        else if (stageFilter === "monitored-invisique") list = list.filter((c) => c.coe_support_compliance === true);
        else if (stageFilter === "monitored-self")      list = list.filter((c) => c.coe_support_compliance === false);
        else if (stageFilter === "cancelled") { /* already scoped above */ }
        else if (stageFilter !== "all") list = list.filter((c) => c._stageKey === stageFilter);

        // Sort — applied last so the search/stage filters drive what's
        // visible first; the sort just orders that subset.
        const ts = (c) => Date.parse(c.submitted_at || c.created_at || 0) || 0;
        // iter101.78 — "Last activity" sort. Uses `status_updated_at`
        // (fallback to updated_at, then created_at) to match what the
        // new Last activity column displays.
        const activityTs = (c) => Date.parse(c.status_updated_at || c.updated_at || c.created_at || 0) || 0;
        const name = (c) => (c.patient_name || "").toLowerCase();
        const stageRank = (c) => PIPELINE_STAGES.findIndex((s) => s.key === c._stageKey);
        const statusKey = (c) => (statusLabelFor(c).label || "").toLowerCase();
        const actionKey = (c) => actionNeededFor(c).text.toLowerCase();
        const progressPctVal = (c) => progressOfCase(c).pct;
        list = [...list];
        switch (sortBy) {
            case "submitted-asc":  list.sort((a, b) => ts(a) - ts(b)); break;
            case "patient-asc":    list.sort((a, b) => name(a).localeCompare(name(b))); break;
            case "patient-desc":   list.sort((a, b) => name(b).localeCompare(name(a))); break;
            case "stage":          list.sort((a, b) => stageRank(a) - stageRank(b)); break;
            // iter98.99ap — new column-header sorts (header clicks).
            case "status-asc":     list.sort((a, b) => statusKey(a).localeCompare(statusKey(b))); break;
            case "status-desc":    list.sort((a, b) => statusKey(b).localeCompare(statusKey(a))); break;
            case "action-asc":     list.sort((a, b) => actionKey(a).localeCompare(actionKey(b))); break;
            case "action-desc":    list.sort((a, b) => actionKey(b).localeCompare(actionKey(a))); break;
            case "progress-asc":   list.sort((a, b) => progressPctVal(a) - progressPctVal(b)); break;
            case "progress-desc":  list.sort((a, b) => progressPctVal(b) - progressPctVal(a)); break;
            // iter101.78 — Last activity sort.
            case "activity-asc":   list.sort((a, b) => activityTs(a) - activityTs(b)); break;
            case "activity-desc":  list.sort((a, b) => activityTs(b) - activityTs(a)); break;
            case "submitted-desc":
            default:               list.sort((a, b) => ts(b) - ts(a)); break;
        }
        return list;
    }, [enriched, cancelledCases, query, stageFilter, sortBy]);

    const setStage = (key) => {
        setStageFilter(key);
        // Mirror to URL so the filter persists across refresh / browser-back
        // and deep-links from the Dashboard ("View all") land correctly.
        const p = new URLSearchParams(searchParams);
        if (key === "all") p.delete("stage"); else p.set("stage", key);
        setSearchParams(p, { replace: true });
        // iter100.78 — Removed the auto `scrollIntoView` on card click.
        // Every KPI + stage card was firing a smooth-scroll to the
        // pipeline-table which yanked the layout upward and made it
        // look like the sticky sidebar "moved" (user report on prod:
        // "click any of those 14 cards, the sidebar still moves").
        // The table sits directly below the KPI/stage rail, so a
        // natural scroll is faster than an involuntary auto-scroll.
    };

    return (
        <div data-testid="doctor-cases-pipeline" className="space-y-5 -mt-2">

            {/* ── Page header ── */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">My Aligner Cases</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Track every patient from impressions to retention.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs"
                        onClick={() => {
                            /* iter100.177 — Alert Rules button used to just flip
                               the view without informing the URL or scrolling to
                               the actual editor, so users landing on the compliance
                               summary couldn't find where thresholds live. Now we
                               route through setViewParam (URL-aware) and jump to
                               the anchored Alert thresholds card once the view
                               mounts. */
                            setViewParam("compliance");
                            setTimeout(() => {
                                document.getElementById("alert-thresholds-anchor")
                                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }, 120);
                        }}
                        data-testid="cases-alert-rules-btn"
                    >
                        <Bell size={13} className="mr-1" /> Alert Rules
                    </Button>
                    <Button size="sm" className="rounded-full" onClick={() => navigate("/doctor/cases/new")} data-testid="cases-new-case-btn">
                        <Plus size={14} className="mr-1" /> New Case
                    </Button>
                </div>
            </div>

            {/* ── 3-view toggle ── */}
            <ViewToggle view={view} setView={setViewParam} kpis={kpis} insightsBuckets={insights.buckets || {}} loading={loading} />

            {/* ── PIPELINE VIEW ── */}
            {view === "pipeline" && (
                <>
                    <KpiInsightBar
                        k={kpis}
                        loading={loading}
                        onClickTotal={() => setStage("all")}
                        onClickFlagged={() => setStage("flagged")}
                        onClickCompliance={() => setViewParam("compliance")}
                    />

                    {/* Single-row stage rail — all 7 stages visible at once,
                        grouped via subtle dividers + faint phase labels. */}
                    <StageRail
                        stages={PIPELINE_STAGES}
                        counts={stageCounts}
                        total={enriched.length}
                        activeKey={stageFilter}
                        onStage={(key) => setStage(stageFilter === key ? "all" : key)}
                    />

                    <FilterBar query={query} setQuery={setQuery} stageFilter={stageFilter} setStage={setStage} kpis={kpis} stageCounts={stageCounts} sortBy={sortBy} setSortBy={setSortBy} loading={loading} />

                    <PipelineTable
                        rows={visible}
                        loading={loading}
                        total={stageFilter === "cancelled" ? cancelledCases.length : enriched.length}
                        onRowClick={openCaseRow}
                        activeCaseId={null}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                    />
                </>
            )}

            {/* ── COMPLIANCE VIEW (Phase 2 — full rebuild from mockup) ── */}
            {view === "compliance" && <CasesPipelineCompliance />}

            {/* ── SCHEDULE VIEW (Phase 3 — full rebuild from mockup) ── */}
            {view === "schedule" && <CasesPipelineSchedule />}

            {/* iter98.99h — Intermediate CasePreviewDrawer popup removed
                per user direction. Drafts now deep-link straight to the
                wizard at the last saved step; non-draft rows open the
                full Case Detail page. The drawer component is still
                defined below in this file as dead code in case we ever
                need to bring it back. */}
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────
   SUBCOMPONENTS
   ────────────────────────────────────────────────────────────────────── */

function ViewToggle({ view, setView, kpis, insightsBuckets, loading }) {
    const atRisk = (insightsBuckets["needs-attention"] || 0) + (insightsBuckets["at-risk"] || 0);
    // iter100.159 — Layout-shift fix. Previously the Pipeline + Compliance
    // badges were rendered conditionally on `kpis.total > 0` / `atRisk > 0`,
    // so the tab strip visibly expanded once the case list finished loading.
    // Badge width is pinned so "0" → "272" doesn't shift either.
    const buttons = [
        { key: "pipeline",   label: "Pipeline",   Icon: FlowArrow,     badge: `${kpis.total || 0}`, badgeTone: "muted",  fixedWidth: true },
        { key: "compliance", label: "Compliance", Icon: ChartLine,     badge: !loading && atRisk > 0 ? `${atRisk} at risk` : null, badgeTone: "danger" },
        { key: "schedule",   label: "Schedule",   Icon: CalendarBlank, badge: null,                 badgeTone: "muted" },
    ];
    return (
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="cases-view-toggle">
            {/* iter101.61 — Editorial teal-tinted segmented rail. Matches
                the CommandHeaderShell's chip-strip grammar: caps labels,
                subtle teal wash background, teal active-state fill, hair-
                thin teal borders. Feels of a piece with the case-detail
                headers rather than a legacy segmented control. */}
            <div
                className="relative inline-flex rounded-2xl border border-teal-100/70 p-1 gap-0.5 overflow-hidden"
                style={{
                    background: "linear-gradient(to bottom, rgba(20,184,166,0.045) 0%, rgba(20,184,166,0.025) 100%)",
                }}
                role="tablist"
                data-testid="cases-toptabs"
            >
                {buttons.map((b) => {
                    const Icon = b.Icon;
                    const active = view === b.key;
                    return (
                        <button
                            key={b.key}
                            role="tab"
                            aria-selected={active}
                            onClick={() => setView(b.key)}
                            className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                                active
                                    ? "bg-primary text-white shadow-sm"
                                    : "text-muted-foreground hover:text-teal-800 hover:bg-teal-50/60"
                            }`}
                            data-testid={`view-toggle-${b.key}`}
                        >
                            <Icon
                                size={13}
                                weight={active ? "fill" : "duotone"}
                                className={active ? "" : "text-teal-700/70"}
                            />
                            <span
                                className={`text-[10.5px] font-bold tracking-[0.14em] uppercase ${
                                    active ? "text-white" : "text-teal-900/70"
                                }`}
                            >
                                {b.label}
                            </span>
                            {b.badge && (
                                <span
                                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums ${
                                        b.fixedWidth ? "min-w-[1.75rem] text-center inline-block" : ""
                                    } ${
                                        b.badgeTone === "danger" && !active
                                            ? "bg-rose-100 text-rose-700"
                                            : active
                                                ? "bg-white/25 text-white"
                                                : "bg-teal-100/70 text-teal-800"
                                    }`}
                                >
                                    {b.badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.14em] uppercase text-muted-foreground/80">
                <ArrowsClockwise size={11} className="text-teal-700/70" />
                {loading ? "Loading…" : "Updated just now"}
            </span>
        </div>
    );
}


function KpiInsightBar({ k, loading, onClickTotal, onClickFlagged, onClickCompliance }) {
    /* iter100.91 — KPI strip trimmed from 6 → 4 cards.
       Dropped cards:
         · "In treatment"  — the same number lives in the Total Cases
           sub-caption ("N in treatment") and again in the Active
           Treatment stage chip below. Three copies of one signal.
         · "Need action"   — same count and same click behaviour as
           the "Doctor Review" stage chip on the pipeline rail
           directly below. Pure duplication.
       Kept cards form a clean 4-card strip mixing the practice-size
       anchor (Total) with three orthogonal clinical KPIs (Flagged,
       Avg Wear, On-Track Rate) — none of which are derivable from
       the stage rail. */
    const cards = [
        { val: k.total,        label: "Total cases",   tone: "foreground", hint: `${k.inTreatment} active`,                                                       onClick: onClickTotal },
        { val: k.flagged,      label: "Flagged cases", tone: "rose",       hint: k.flagged > 0 ? "Compliance risk" : "All clear",                                onClick: onClickFlagged },
        { val: k.avgWear != null ? `${k.avgWear.toFixed(1)}h` : "—", label: "Avg wear / day", tone: k.avgWear == null ? "muted" : (k.avgWear < 16 ? "rose" : k.avgWear < 20 ? "amber" : "primary"), hint: k.avgWear != null ? "Target 22h/day" : "No data yet", onClick: onClickCompliance },
        { val: k.onTrackPct != null ? `${k.onTrackPct}%` : "—",       label: "On-track rate", tone: "primary",                                                  hint: k.onTrackPct != null ? "Compliance bucket" : "No active cases", onClick: onClickCompliance },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="pipeline-kpis">
            {cards.map((c, i) => (
                <button
                    key={i}
                    type="button"
                    onClick={c.onClick}
                    className="text-left bg-card border border-border rounded-xl px-4 py-3 transition-all cursor-pointer hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    data-testid={`kpi-${c.label.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "")}`}
                    title={`Open ${c.label.toLowerCase()}`}
                >
                    <div className={`text-xl font-bold tabular-nums leading-none ${
                        c.tone === "primary" ? "text-primary"
                        : c.tone === "amber" ? "text-amber-700"
                        : c.tone === "rose" ? "text-rose-600"
                        : c.tone === "muted" ? "text-muted-foreground"
                        : "text-foreground"
                    }`}>
                        {loading
                            ? (<span className="inline-block h-4 w-8 bg-secondary rounded animate-pulse align-middle" aria-hidden />)
                            : c.val
                        }
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{c.label}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{c.hint}</div>
                </button>
            ))}
        </div>
    );
}


/* StageRail — iter100.281 · Redesigned stage filter strip.
 *
 * Previously used `overflow-x-auto` with a fixed min-width per chip,
 * which produced an ugly grey scrollbar the moment there were more
 * than ~6 stages (i.e. always). Now a responsive CSS grid: 4 cols on
 * tablets → 8 cols on wide desktops. Chips wrap gracefully to 2 rows
 * without needing horizontal scroll. Each chip gets a subtle
 * stage-tone top-rail so the eye can group them by phase (intake /
 * active / complete) without a divider taking up real estate.
 */
function StageRail({ stages, counts, total, activeKey, onStage }) {
    return (
        /* iter101.61 — Editorial teal-tinted stage rail. Same visual
           DNA as CommandHeaderShell's Zone B: teal wash, hair-thin teal
           border, chip cells with editorial caps labels. Semantic tone
           colors are still honoured per-stage (draft=violet, rush=amber,
           etc) but framed inside the shared editorial container. */
        <div
            className="relative rounded-2xl border border-teal-100/70 p-1.5 grid gap-1 grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-10 overflow-hidden"
            style={{
                background: "linear-gradient(to bottom, rgba(20,184,166,0.045) 0%, rgba(20,184,166,0.02) 100%)",
            }}
            data-testid="pipeline-stage-cards"
        >
            {stages.map((s) => (
                <StageChip
                    key={s.key}
                    stage={s}
                    count={counts[s.key] || 0}
                    total={total}
                    active={activeKey === s.key}
                    onClick={() => onStage(s.key)}
                />
            ))}
        </div>
    );
}


function StageChip({ stage, count, total, active, onClick }) {
    const tone = TONE_STYLES[stage.tone];
    void total;
    const nonZero = count > 0;
    /* Editorial chip: caps label above, big count below. Active state
       gets a teal fill + white text. Inactive non-zero states surface
       the stage's tone via the left rail. Zero states fade to slate. */
    return (
        <button
            type="button"
            onClick={onClick}
            className={`group relative text-left rounded-xl px-3 py-2.5 transition-all overflow-hidden ${
                active
                    ? "bg-primary text-white shadow-sm"
                    : nonZero
                        ? "bg-card hover:bg-teal-50/60 hover:-translate-y-[0.5px] hover:shadow-sm ring-1 ring-teal-100/70"
                        : "bg-card/50 hover:bg-teal-50/40 ring-1 ring-teal-100/40"
            }`}
            data-testid={`stage-card-${stage.key}`}
            title={stage.sub}
            aria-pressed={active}
        >
            {/* Left rail — narrow colored strip using the stage's tone.
                Suppressed on active state (the whole card is teal). */}
            {!active && (
                <span
                    aria-hidden
                    className={`absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full ${tone.topRail} ${nonZero ? "opacity-90" : "opacity-30"}`}
                />
            )}
            <div className="pl-2">
                <div
                    className={`text-[9px] font-bold tracking-[0.14em] uppercase leading-tight ${
                        active
                            ? "text-white/85"
                            : nonZero
                                ? "text-teal-800/70"
                                : "text-muted-foreground/60"
                    }`}
                >
                    {stage.label}
                </div>
                <div
                    className={`mt-1 text-lg font-bold leading-none tabular-nums ${
                        active
                            ? "text-white"
                            : nonZero
                                ? tone.text
                                : "text-muted-foreground/40"
                    }`}
                >
                    {count}
                </div>
            </div>
        </button>
    );
}


function FilterBar({ query, setQuery, stageFilter, setStage, kpis, sortBy, setSortBy, loading }) {
    const sortOptions = [
        { id: "submitted-desc", label: "Newest first" },
        { id: "submitted-asc",  label: "Oldest first" },
        { id: "patient-asc",    label: "Patient A → Z" },
        { id: "patient-desc",   label: "Patient Z → A" },
        { id: "stage",          label: "By pipeline stage" },
        { id: "status-asc",     label: "Status A → Z" },
        { id: "status-desc",    label: "Status Z → A" },
        { id: "action-asc",     label: "Action A → Z" },
        { id: "action-desc",    label: "Action Z → A" },
        { id: "progress-desc",  label: "Most progress first" },
        { id: "progress-asc",   label: "Least progress first" },
    ];
    const sortLabel = sortOptions.find((s) => s.id === sortBy)?.label || "Sort";
    const chips = [
        { key: "all", label: "All cases", count: kpis.total, tone: "primary" },
        { key: "flagged", label: "Flagged", count: kpis.flagged, tone: "rose" },
        // iter98.99dh — Compliance & Monitoring 2-state filter cluster.
        // Only surfaces a chip when at least one case sits in that mode
        // so brand-new doctors with zero submissions don't see empty
        // chrome. iter100.160 briefly reserved these during loading to
        // avoid a CLS shift, but that caused a *reverse* shift (chip
        // disappearing) on accounts that end up with 0 in the bucket —
        // worse UX than the original one-time expansion.
        ...(kpis.monitoredInvisique > 0
            ? [{ key: "monitored-invisique", label: "Invisique-monitored", count: kpis.monitoredInvisique, tone: "primary", Icon: ShieldCheck }]
            : []),
        ...(kpis.monitoredSelf > 0
            ? [{ key: "monitored-self", label: "Self-monitored", count: kpis.monitoredSelf, tone: "slate", Icon: Stethoscope }]
            : []),
    ];
    return (
        <>
            {/* iter100.197 · Shared FilterSearchBar primitive locks
                height + font-size + placeholder styling identical to
                every other doctor-portal list surface. Sort dropdown
                lives in the right slot; chip row renders below. */}
            <FilterSearchBar
                query={query}
                setQuery={setQuery}
                placeholder="Search patient, case ID…"
                searchTestid="pipeline-search-input"
                testid="pipeline-filter-bar"
                rightSlot={
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
                                title="Sort cases"
                                data-testid="pipeline-sort-btn"
                            >
                                <ArrowsDownUp size={12} /> {sortLabel}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[170px]">
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Sort by</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
                                {sortOptions.map((opt) => (
                                    <DropdownMenuRadioItem
                                        key={opt.id}
                                        value={opt.id}
                                        className="text-xs"
                                        data-testid={`pipeline-sort-${opt.id}`}
                                    >
                                        {opt.label}
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                }
            />
            <FilterChipRow
                value={stageFilter}
                onChange={setStage}
                testid="pipeline-filter-chips"
                chipTestidPrefix="pipeline-chip"
                chips={chips.map((c) => ({
                    id:    c.key,
                    label: c.label,
                    count: c.count,
                    tone:  c.tone || "primary",
                    Icon:  c.Icon,
                }))}
            />
        </>
    );
}


function PipelineTable({ rows, loading, total, onRowClick, activeCaseId, sortBy, setSortBy }) {
    // iter101.78 — Client-side infinite scroll. Doctors with 200+ cases
    // felt the page lag as every row rendered at once. Now we render
    // in windows of 25 and grow the window via an IntersectionObserver
    // sentinel row at the bottom. Backend still returns the full list
    // (up to 500 per doctor per current API contract) so this is a
    // pure-frontend UX improvement — no risky API changes required.
    // The sentinel is skipped in tests / no-JS scenarios by keeping
    // the "Load more" button as a visible fallback.
    const PAGE_SIZE = 25;
    const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
    const sentinelRef = React.useRef(null);

    // Reset the window whenever filters / sort change the row set so the
    // doctor never scrolls into stale positions.
    React.useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [rows.length, sortBy]);

    React.useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return undefined;
        if (visibleCount >= rows.length) return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setVisibleCount((v) => Math.min(v + PAGE_SIZE, rows.length));
                }
            },
            { rootMargin: "120px" },
        );
        io.observe(node);
        return () => io.disconnect();
    }, [visibleCount, rows.length]);

    const visibleRows = rows.slice(0, visibleCount);
    const hasMore = visibleCount < rows.length;
    /* iter98.99ap — Header sort handler. `setSortBy` toggles between
       the asc/desc variants of the active column when the user clicks
       the same column header twice. Default first-click direction:
       desc for date/progress, asc for text. */
    const toggleSort = (key) => {
        const map = {
            patient:            { asc: "patient-asc",    desc: "patient-desc",    firstClick: "patient-asc" },
            status:             { asc: "status-asc",     desc: "status-desc",     firstClick: "status-asc" },
            action_needed:      { asc: "action-asc",     desc: "action-desc",     firstClick: "action-asc" },
            treatment_progress: { asc: "progress-asc",   desc: "progress-desc",   firstClick: "progress-desc" },
            last_activity:      { asc: "activity-asc",   desc: "activity-desc",   firstClick: "activity-desc" },
            submitted_on:       { asc: "submitted-asc",  desc: "submitted-desc",  firstClick: "submitted-desc" },
        }[key];
        if (!map) return;
        if (sortBy === map.asc) setSortBy(map.desc);
        else if (sortBy === map.desc) setSortBy(map.asc);
        else setSortBy(map.firstClick);
    };
    const sortIndicator = (key) => {
        const lookup = {
            patient:            ["patient-asc", "patient-desc"],
            status:             ["status-asc", "status-desc"],
            action_needed:      ["action-asc", "action-desc"],
            treatment_progress: ["progress-asc", "progress-desc"],
            last_activity:      ["activity-asc", "activity-desc"],
            submitted_on:       ["submitted-asc", "submitted-desc"],
        }[key] || [];
        if (sortBy === lookup[0]) return "▲";
        if (sortBy === lookup[1]) return "▼";
        return "";
    };

    return (
        <div id="pipeline-table" className="bg-card border border-border rounded-xl overflow-hidden" data-testid="pipeline-table">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                <div className="inline-flex items-center gap-2 text-sm font-medium">
                    <ListBullets size={15} className="text-primary" weight="duotone" />
                    Cases
                    <span className="text-[10px] text-muted-foreground bg-secondary rounded-full px-2 py-0.5" data-testid="pipeline-count-chip">
                        {visibleRows.length} of {rows.length}{rows.length !== total ? ` · ${total} total` : ""}
                    </span>
                </div>
                <span className="text-[10px] text-muted-foreground">Click a row to open · Click a column header to sort</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                    <thead className="bg-secondary/60">
                        <tr>
                            <ThSort onClick={() => toggleSort("patient")}            testid="col-patient">Patient (+ Case ID) {sortIndicator("patient")}</ThSort>
                            <ThSort onClick={() => toggleSort("status")}             testid="col-status">Status {sortIndicator("status")}</ThSort>
                            <ThSort onClick={() => toggleSort("treatment_progress")} testid="col-treatment_progress">Treatment progress {sortIndicator("treatment_progress")}</ThSort>
                            <ThSort onClick={() => toggleSort("last_activity")}      testid="col-last_activity">Last activity {sortIndicator("last_activity")}</ThSort>
                            <ThSort onClick={() => toggleSort("submitted_on")}       testid="col-created_on">Created on {sortIndicator("submitted_on")}</ThSort>
                            <Th className="w-20 text-right">Action</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">Loading cases…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-12 text-center"><EmptyState /></td></tr>
                        )}
                        {!loading && visibleRows.map((c) => (
                            <PipelineRow
                                key={c.id}
                                case_={c}
                                active={activeCaseId === c.id}
                                onClick={() => onRowClick(c)}
                            />
                        ))}
                        {/* iter101.78 — Infinite-scroll sentinel + "Load
                            more" fallback. Sentinel row auto-fires
                            visibleCount++ once it enters the viewport
                            (IntersectionObserver). Button below is
                            keyboard-accessible + a fallback for browsers
                            without observer support. */}
                        {!loading && hasMore && (
                            <tr ref={sentinelRef} data-testid="pipeline-infinite-sentinel">
                                <td colSpan={6} className="px-4 py-4 text-center">
                                    <button
                                        type="button"
                                        onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, rows.length))}
                                        className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary transition-colors font-medium"
                                        data-testid="pipeline-load-more-btn"
                                    >
                                        <ArrowsClockwise size={11} weight="bold" />
                                        Load next {Math.min(PAGE_SIZE, rows.length - visibleCount)} · Showing {visibleCount} of {rows.length}
                                    </button>
                                </td>
                            </tr>
                        )}
                        {!loading && !hasMore && rows.length > PAGE_SIZE && (
                            <tr>
                                <td colSpan={6} className="px-4 py-3 text-center text-[10.5px] text-muted-foreground/70">
                                    Showing all {rows.length} of {total} — you&apos;re caught up.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


const Th = ({ children, className = "" }) => (
    <th className={`text-left px-4 py-2.5 font-semibold text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border ${className}`}>{children}</th>
);

const ThSort = ({ children, onClick, testid, className = "" }) => (
    <th className={`text-left px-4 py-2.5 font-semibold text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border ${className}`}>
        <button type="button" onClick={onClick} className="inline-flex items-center gap-1 hover:text-foreground transition-colors" data-testid={testid}>
            {children}
        </button>
    </th>
);


function PipelineRow({ case_, active, onClick }) {
    const c = case_;
    const tint = avatarTintFor(c.patient_name);
    const status = statusLabelFor(c);
    const progress = progressOfCase(c);
    // iter101.77 — Table now surfaces "Created on" (kickoff moment) rather
    // than the ambiguous submitted_at (which is null for drafts). This
    // matches the "Created on" column header + the natural mental model
    // doctors have when triaging a long drafts list.
    const createdIso = c.created_at || c.submitted_at;
    // iter101.86 — Progress tooltip now also renders for scan-visit
    // cases (scan_visit_scheduled / scan_visit_completed). The doctor
    // still has an incomplete wizard to finish after the technician
    // uploads scans — same 6-step progress model applies. `isDraftLike`
    // groups all three statuses so the row logic (progress column +
    // tooltip + Resume icon) shares one branch.
    const isDraft = c.status === "draft";
    const isScanVisit = c.status === "scan_visit_scheduled" || c.status === "scan_visit_completed";
    const isDraftLike = isDraft || isScanVisit;
    const navigate = useNavigate();
    // iter101.83 — Row-level progress display. `stepsDone` and `total`
    // come from `computeDraftProgress` (honest count of filled wizard
    // steps, forgiving toward out-of-order authoring). `resumeStep` is
    // kept as-is because the "Step X next" hint + the Resume icon's
    // deep-link both need the strict first-incomplete step.
    // iter101.84 — `steps` array powers the hover tooltip breakdown.
    // iter101.86 — Both isDraft (true drafts) and isScanVisit (visit
    // scheduled / completed) share the same progress read-out.
    const resumeStep = computeResumeStep(c);
    const { done: stepsDone, total: totalSteps, steps: stepFlags } = computeDraftProgress(c);
    // iter101.77 — Near-complete drafts (5/6 or 6/6) flip the progress
    // bar tone to emerald and get an "Almost ready" tag so doctors
    // instinctively finish those first.
    const isAlmostReady = isDraftLike && stepsDone >= totalSteps - 1;
    return (
        <tr
            onClick={onClick}
            className={`group cursor-pointer border-b border-border/60 last:border-b-0 transition-colors ${
                active ? "bg-primary/10" : c._flagged ? "bg-amber-50/40 hover:bg-amber-50/70" : "hover:bg-secondary/60"
            }`}
            data-testid={`pipeline-row-${c.id}`}
        >
            {/* Patient (+ Case ID) — avatar · name · case code. iter101.77:
                patient PID chip removed from the row; the case code alone
                is enough to disambiguate. Frees horizontal room for the
                new Action column. */}
            <td className="px-4 py-2.5" data-testid={`case-row-${c.id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                    {c.patient_photo_data_url ? (
                        <img src={c.patient_photo_data_url} alt={c.patient_name} className={`w-9 h-9 rounded-full object-cover border ${tint.border} shrink-0`} />
                    ) : (
                        <div className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-semibold border shrink-0 ${tint.bg} ${tint.border} ${tint.text}`}>
                            {initialsOf(c.patient_name)}
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="font-medium text-foreground truncate flex items-center gap-1.5">
                            <span className="truncate">{c.patient_name || "Patient"}</span>
                            <MonitoringMarker mode={c.coe_support_compliance} caseId={c.id} status={c.status} />
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                            <span className="font-mono text-foreground/80">{c.code || c.case_code || "—"}</span>
                        </div>
                    </div>
                </div>
            </td>

            {/* Status — canonical pill + optional muted sub-label. iter101.78
                — Splits on " · " so long labels like "Approved · Preparing
                Production" render as a compact pill (headline) with the
                secondary phrase on a second muted line. Never breaks the
                grid regardless of label length. */}
            <td className="px-4 py-2.5 max-w-[180px]">
                {(() => {
                    const { headline, detail } = splitStatusLabel(status.label);
                    return (
                        <div className="flex flex-col gap-1 items-start min-w-0">
                            <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider text-[10px] ${STATUS_TONE_CLS[status.tone] || STATUS_TONE_CLS.neutral}`}
                                data-testid={`case-status-${c.id}`}
                                title={detail ? `${headline} — ${detail}` : headline}
                            >
                                <span className="truncate max-w-[140px]">{headline}</span>
                            </span>
                            {detail && (
                                <span
                                    className="text-[10.5px] text-muted-foreground leading-tight truncate max-w-[160px]"
                                    title={detail}
                                    data-testid={`case-status-detail-${c.id}`}
                                >
                                    {detail}
                                </span>
                            )}
                            {isAlmostReady && (
                                <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.14em] bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                    data-testid={`almost-ready-tag-${c.id}`}
                                >
                                    <Sparkle size={9} weight="fill" /> Almost ready to submit
                                </span>
                            )}
                        </div>
                    );
                })()}
            </td>

            {/* Treatment progress — for drafts we show wizard progress
                (X of 6 steps done · Step Y next). For live cases we show
                aligner X/Y + bar. iter101.77 — Near-complete drafts flip
                the bar to emerald so the "almost done" story reads even
                without opening the row. iter101.83 — `stepsDone` now
                uses `computeDraftProgress` (forgiving heuristic), so the
                counter honestly reflects fills even if Step 1 isn't
                tight-complete yet. iter101.84 — Hover tooltip breaks
                the count down step-by-step (✓ Done · ○ Pending) so the
                doctor can pinpoint what's missing without opening the
                case. */}
            <td className="px-4 py-2.5 min-w-[140px]" data-testid={`case-progress-${c.id}`}>
                {isDraftLike ? (() => {
                    const pct = Math.round((stepsDone / totalSteps) * 100);
                    const nextLabel = resumeStep > totalSteps
                        ? "Review & Submit"
                        : `Step ${resumeStep}`;
                    const barTone = isAlmostReady ? "bg-emerald-500" : "bg-amber-500";
                    return (
                        <TooltipProvider delayDuration={120}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="space-y-1 cursor-help" data-testid={`draft-progress-hover-${c.id}`}>
                                        <div className="flex items-center gap-1.5 text-[11px] text-foreground/80 tabular-nums">
                                            <span className={`font-semibold ${isAlmostReady ? "text-emerald-700" : ""}`}>{stepsDone}/{totalSteps}</span>
                                            <span className="text-muted-foreground truncate">steps · {nextLabel} next</span>
                                        </div>
                                        <div className="w-full bg-secondary rounded-full h-1.5">
                                            <div
                                                className={`h-1.5 rounded-full transition-all ${barTone}`}
                                                style={{ width: `${pct}%` }}
                                                data-testid={`draft-progress-bar-${c.id}`}
                                            />
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="bottom"
                                    align="start"
                                    className="bg-card text-foreground border border-border shadow-lg p-0 max-w-[240px]"
                                    data-testid={`draft-progress-tooltip-${c.id}`}
                                >
                                    <div className="px-3 py-2.5 border-b border-border/60 flex items-center justify-between gap-4">
                                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                            Wizard progress
                                        </div>
                                        <div className={`text-[10.5px] font-bold tabular-nums px-1.5 py-0.5 rounded-full ${
                                            isAlmostReady
                                                ? "text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200"
                                                : "text-amber-800 bg-amber-50 ring-1 ring-amber-200"
                                        }`}>
                                            {stepsDone} / {totalSteps}
                                        </div>
                                    </div>
                                    <ul className="px-2.5 py-2 space-y-1">
                                        {/* iter101.87 — Ghost row for scan-visit
                                            cases: surfaces the technician
                                            visit date/time (+ tech name if
                                            assigned) so doctors instantly see
                                            who they're waiting on without
                                            opening the case. Shows above the
                                            6 wizard steps because the visit is
                                            a prerequisite for the doctor to
                                            resume filling in Steps 2-6. */}
                                        {isScanVisit && (
                                            <li className="flex items-start gap-2 text-[11.5px] pb-1.5 mb-1 border-b border-border/50">
                                                <span className={`shrink-0 mt-0.5 w-4 h-4 rounded-full grid place-items-center ${
                                                    c.status === "scan_visit_completed"
                                                        ? "bg-emerald-100 text-emerald-700"
                                                        : "bg-amber-100 text-amber-700"
                                                }`}>
                                                    {c.status === "scan_visit_completed"
                                                        ? <CheckCircle size={10} weight="fill" />
                                                        : <Clock size={9} weight="bold" />}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <div className={`font-semibold leading-tight ${
                                                        c.status === "scan_visit_completed"
                                                            ? "text-foreground/90"
                                                            : "text-amber-900"
                                                    }`}>
                                                        {c.status === "scan_visit_completed"
                                                            ? "Scan visit — completed"
                                                            : "Awaiting scan visit"}
                                                    </div>
                                                    <div className="text-[10.5px] text-muted-foreground leading-snug">
                                                        {formatVisitLine(c.scan_visit_date, c.scan_visit_time, c.scan_visit_technician_name)}
                                                    </div>
                                                </div>
                                            </li>
                                        )}
                                        {stepFlags.map((s) => (
                                            <li key={s.id} className="flex items-center gap-2 text-[11.5px]">
                                                <span className={`shrink-0 w-4 h-4 rounded-full grid place-items-center ${
                                                    s.done
                                                        ? "bg-emerald-100 text-emerald-700"
                                                        : "bg-muted/70 text-muted-foreground/50"
                                                }`}>
                                                    {s.done
                                                        ? <CheckCircle size={10} weight="fill" />
                                                        : <CircleIcon size={7} weight="regular" />}
                                                </span>
                                                <span className={`flex-1 truncate ${s.done ? "text-foreground/90 font-medium" : "text-muted-foreground/85"}`}>
                                                    {s.label}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                })() : progress.total > 0 ? (
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-[11px] text-foreground/80 tabular-nums">
                            <span className="font-semibold">{progress.stage}/{progress.total}</span>
                            <span className="text-muted-foreground">aligner</span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-1.5">
                            <div
                                className="h-1.5 rounded-full bg-primary transition-all"
                                style={{ width: `${progress.pct}%` }}
                            />
                        </div>
                    </div>
                ) : (
                    <span className="text-xs text-muted-foreground/60">—</span>
                )}
            </td>

            {/* Last activity — iter101.78. Relative time since the case
                last transitioned status (or was created for drafts). More
                useful than absolute date for triage; rows stale > 5 days
                render in rose so the doctor spots forgotten cases at
                a glance. */}
            <td className="px-4 py-2.5 text-[11px] min-w-[100px]" data-testid={`case-last-activity-${c.id}`}>
                {(() => {
                    const iso = c.status_updated_at || c.updated_at || c.created_at;
                    const { text, stale } = formatLastActivity(iso);
                    return (
                        <span
                            className={`tabular-nums font-medium ${stale ? "text-rose-600" : "text-foreground/80"}`}
                            title={iso ? new Date(iso).toLocaleString("en-IN") : ""}
                        >
                            {text}
                        </span>
                    );
                })()}
            </td>

            {/* Created on */}
            <td className="px-4 py-2.5 text-[12px] text-foreground/80">
                {formatSubmittedOn(createdIso)}
            </td>

            {/* Action — iter101.77. Persistent icon-only column (previously
                a hover-only Preview/Open combo). Drafts get a Resume icon
                that deep-links to the correct step; live cases get a
                subtle chevron that mirrors the row-click behaviour. */}
            <td className="px-4 py-2.5">
                <div className="flex items-center justify-end">
                    {isDraft ? (
                        <RowAct
                            title={`Resume drafting · Step ${resumeStep}`}
                            tone={isAlmostReady ? "emerald" : "primary"}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/doctor/cases/new?draft=${c.id}&step=${resumeStep}`);
                            }}
                            testid={`case-row-resume-${c.id}`}
                        >
                            <PencilLine size={13} weight="bold" />
                        </RowAct>
                    ) : (
                        <RowAct
                            title="Open case"
                            tone="neutral"
                            onClick={(e) => { e.stopPropagation(); onClick(); }}
                            testid={`case-row-open-${c.id}`}
                        >
                            <CaretRight size={13} weight="bold" />
                        </RowAct>
                    )}
                </div>
            </td>
        </tr>
    );
}


function RowAct({ children, onClick, title, tone = "neutral", testid }) {
    // iter101.77 — Added emerald tone for near-complete drafts + optional
    // data-testid so the row-level Resume/Open button is addressable in
    // e2e tests.
    const toneCls = tone === "primary"
        ? "border-border bg-card hover:bg-primary/10 hover:border-primary/40 hover:text-primary text-muted-foreground"
        : tone === "emerald"
            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300"
            : "border-border bg-card hover:bg-secondary text-muted-foreground";
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            data-testid={testid}
            className={`w-7 h-7 rounded-md border grid place-items-center transition-colors ${toneCls}`}
        >
            {children}
        </button>
    );
}


function EmptyState() {
    return (
        <div className="py-6 text-center">
            <div className="w-12 h-12 rounded-full bg-secondary grid place-items-center mx-auto mb-2">
                <MagnifyingGlass size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium">No cases match this filter</p>
            <p className="text-xs text-muted-foreground mt-1">Try clearing the search or selecting a different stage.</p>
        </div>
    );
}


function formatRelative(iso) {
    try {
        const d = new Date(iso.length === 10 ? iso + "T00:00:00Z" : iso);
        const days = Math.floor((Date.now() - d.getTime()) / 86400000);
        if (days < 0) return "today";
        if (days === 0) return "today";
        if (days === 1) return "1d ago";
        if (days < 7) return `${days}d ago`;
        if (days < 30) return `${Math.floor(days / 7)}w ago`;
        return d.toLocaleDateString([], { day: "2-digit", month: "short" });
    } catch { return "—"; }
}


/* ──────────────────────────────────────────────────────────────────────
   CASE PREVIEW DRAWER — slides in from the right when a pipeline row is
   clicked. Compact summary + quick actions; "Open full case" links to
   the existing /doctor/cases/:id detail page.
   ────────────────────────────────────────────────────────────────────── */
/* CasePreviewDrawer — iter98.83 redesign.
 *
 * Doctor's at-a-glance case sheet. Replaces the previous 2-column
 * "Treatment progress + Identifiers" stack with an info-dense layout:
 *
 *   • Roomier hero with a 64px avatar, prominent stage chip
 *   • 3 KPI tiles (Aligner · Compliance · Stage) for at-a-glance scan
 *   • Horizontal lifecycle stepper showing the whole pipeline at once
 *     with the current stage highlighted (intake → active → completion)
 *   • Metadata strip with the 4 identifiers as inline pills
 *   • Single primary CTA "Open full case →" (removed the confusing
 *     "Nudge" button — it routed to chat but the label didn't match)
 *
 * No new data fetched — purely a re-presentation of the existing
 * `case_` + enriched `_ins` insights payload.
 */
function CasePreviewDrawer({ open, case_, onClose, onOpenFull }) {
    const c = case_;
    const stage = c ? PIPELINE_STAGES.find((s) => s.key === c._stageKey) : null;
    const tone = stage ? TONE_STYLES[stage.tone] : TONE_STYLES.neutral;
    const tint = c ? avatarTintFor(c.patient_name) : null;
    const ins = (c && c._ins) || {};
    const treatPct = ins.total_stages > 0 && ins.stage > 0 ? Math.round((ins.stage / ins.total_stages) * 100) : null;

    // Lifecycle stepper: figure out the index of the current stage in
    // the full pipeline so we can render "done"/"active"/"pending" dots.
    const currentIdx = stage ? PIPELINE_STAGES.findIndex((s) => s.key === stage.key) : -1;

    // Bucket → human label & badge class (mirrors the existing compliance
    // colour vocab so the doctor reads it the same way across the app).
    const bucketLabel =
        ins.bucket === "needs-attention" ? "Needs attention" :
        ins.bucket === "at-risk"         ? "At risk"         :
        ins.bucket === "on-track"        ? "On track"        : "Pending";
    const bucketCls =
        ins.bucket === "needs-attention" ? "bg-rose-50 text-rose-700 border-rose-200"   :
        ins.bucket === "at-risk"         ? "bg-amber-50 text-amber-700 border-amber-200":
        ins.bucket === "on-track"        ? "bg-primary/10 text-primary border-primary/30":
        "bg-secondary text-muted-foreground border-border";

    return (
        <Dialog open={open && !!case_} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                className="max-w-2xl p-0 gap-0 overflow-hidden"
                data-testid="case-preview-drawer"
            >
                {!c ? null : (
                    <>
                        {/* Hero — bigger, calmer. Avatar 64px. Stage
                            chip moved (iter98.84) below the Case ID line
                            inside the text column so it reads as part of
                            the patient/case identity, not floating in
                            the corner. */}
                        <DialogHeader className="px-6 py-5 border-b border-border bg-gradient-to-br from-primary/8 via-primary/3 to-card">
                            <div className="flex items-start gap-4">
                                {c.patient_photo_data_url ? (
                                    <img src={c.patient_photo_data_url} alt={c.patient_name} className={`w-16 h-16 rounded-full object-cover border-2 ${tint.border} shrink-0`} />
                                ) : (
                                    <div className={`w-16 h-16 rounded-full grid place-items-center text-base font-semibold border-2 shrink-0 ${tint.bg} ${tint.border} ${tint.text}`}>
                                        {initialsOf(c.patient_name)}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0 text-left">
                                    <DialogTitle className="text-[17px] font-bold tracking-tight truncate text-left" data-testid="drawer-patient-name">
                                        {c.patient_name || "Patient"}
                                    </DialogTitle>
                                    <DialogDescription className="text-[12px] text-muted-foreground truncate text-left mt-1 flex items-center gap-1.5 flex-wrap">
                                        <IdentificationCard size={12} weight="duotone" />
                                        <span className="font-mono">{c.code || c.case_code}</span>
                                        <span className="text-muted-foreground/50">·</span>
                                        <span>{c.practice_location_name || c.clinic_name || "Primary practice"}</span>
                                    </DialogDescription>
                                    {stage && (
                                        <span
                                            className={`inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.06em] px-2.5 py-1 rounded-full border mt-2 ${tone.chip}`}
                                            data-testid="drawer-stage-chip"
                                        >
                                            {stage.label}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </DialogHeader>

                        <div className="max-h-[68vh] overflow-y-auto px-6 py-5 space-y-5">
                            {/* KPI tiles — three at-a-glance numbers replace
                                the old fragmented "progress + compliance"
                                sections. Each tile is its own focal point. */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="drawer-kpi-grid">
                                {/* ALIGNER */}
                                <KpiTile
                                    icon={<Tooth size={13} weight="duotone" />}
                                    eyebrow="Aligner"
                                    primary={treatPct != null ? `${ins.stage} / ${ins.total_stages}` : "—"}
                                    detail={treatPct != null ? `${treatPct}% complete` : "Not yet started"}
                                >
                                    {treatPct != null && (
                                        <div className="h-1.5 rounded-full bg-secondary overflow-hidden mt-2">
                                            <div className="h-full rounded-full bg-primary" style={{ width: `${treatPct}%` }} />
                                        </div>
                                    )}
                                </KpiTile>

                                {/* COMPLIANCE */}
                                <KpiTile
                                    icon={<Heartbeat size={13} weight="duotone" />}
                                    eyebrow="Compliance"
                                    primary={ins.wear_7d_avg != null ? `${ins.wear_7d_avg}h` : "—"}
                                    detail={ins.wear_7d_avg != null ? "/ day · 7d avg" : "Awaiting check-ins"}
                                    primaryTone={ins.wear_7d_avg == null ? "muted" : ins.wear_7d_avg < 16 ? "rose" : ins.wear_7d_avg < 20 ? "amber" : "primary"}
                                >
                                    {ins.wear_30d_series && <WearMiniChart series={ins.wear_30d_series.slice(-14)} />}
                                    {ins.bucket && (
                                        <span className={`mt-1 inline-block text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full border ${bucketCls}`}>
                                            {bucketLabel}
                                        </span>
                                    )}
                                </KpiTile>

                                {/* DAYS IN STAGE */}
                                <KpiTile
                                    icon={<Clock size={13} weight="duotone" />}
                                    eyebrow="Days in stage"
                                    primary={c._days_in_stage != null ? `${c._days_in_stage}d` : "—"}
                                    detail={ins.stage_day ? `Aligner day ${ins.stage_day}` : (stage?.sub || "")}
                                />
                            </div>

                            {/* Lifecycle stepper — horizontal pipeline of
                                all PIPELINE_STAGES with the active stage
                                highlighted. Done / current / pending states
                                each rendered with their own dot weight. */}
                            <div className="rounded-lg border border-border bg-card p-4" data-testid="drawer-lifecycle">
                                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-3 flex items-center gap-1.5">
                                    <Path size={11} weight="duotone" /> Case lifecycle
                                </div>
                                <ol className="grid grid-cols-7 gap-1" role="list">
                                    {PIPELINE_STAGES.map((s, idx) => {
                                        const isDone   = currentIdx !== -1 && idx < currentIdx;
                                        const isActive = currentIdx !== -1 && idx === currentIdx;
                                        return (
                                            <li
                                                key={s.key}
                                                className="flex flex-col items-center gap-1.5 text-center relative"
                                                data-testid={`lifecycle-step-${s.key}`}
                                                data-active={isActive ? "true" : "false"}
                                            >
                                                {/* Connector segment to the previous dot */}
                                                {idx > 0 && (
                                                    <span
                                                        aria-hidden
                                                        className={`absolute top-[7px] right-1/2 w-full h-px ${
                                                            isDone || isActive ? "bg-primary/50" : "bg-border"
                                                        }`}
                                                    />
                                                )}
                                                {isActive ? (
                                                    <span className="relative z-10 w-3.5 h-3.5 rounded-full bg-primary ring-4 ring-primary/15 grid place-items-center">
                                                        <span className="w-1 h-1 bg-white rounded-full" />
                                                    </span>
                                                ) : isDone ? (
                                                    <CheckCircle size={14} weight="fill" className="relative z-10 text-primary" />
                                                ) : (
                                                    <CircleIcon size={14} weight="bold" className="relative z-10 text-muted-foreground/40 bg-card" />
                                                )}
                                                <span
                                                    className={`text-[9.5px] leading-tight font-medium ${
                                                        isActive ? "text-foreground font-bold" : isDone ? "text-primary" : "text-muted-foreground"
                                                    }`}
                                                    title={s.label}
                                                >
                                                    {s.label}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ol>
                            </div>

                            {/* Metadata strip — identifiers as 4 inline
                                pills. Replaces the old "Identifiers" box. */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="drawer-meta-strip">
                                <MetaPill icon={<IdentificationCard size={11} weight="duotone" />} label="Patient ID" value={humanPatientId(c.patient_id) || "—"} />
                                <MetaPill icon={<MapPin size={11} weight="duotone" />} label="Practice" value={c.practice_location_name || c.clinic_name || "Primary"} />
                                <MetaPill icon={<CalendarBlank size={11} weight="duotone" />} label="Added" value={c.created_at ? new Date(c.created_at).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—"} />
                                <MetaPill icon={<Bell size={11} weight="duotone" />} label="Last touch" value={ins.last_touch_at ? formatRelative(ins.last_touch_at) : "—"} />
                            </div>
                        </div>

                        {/* Footer — single primary CTA. The previous
                            "Nudge" button is removed; chat is one click
                            inside the full case page. */}
                        <div className="px-6 py-3.5 border-t border-border bg-secondary/30">
                            <Button
                                onClick={() => onOpenFull(c)}
                                className="w-full rounded-md"
                                data-testid="drawer-open-full-btn"
                            >
                                <ArrowSquareOut size={13} className="mr-1.5" /> Open full case
                            </Button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}


/* iter98.83 — KPI tile used by the case-preview drawer. Big number on
 * top + small eyebrow + detail line + an optional `children` slot for
 * a sparkline / progress bar / bucket pill underneath. */
function KpiTile({ icon, eyebrow, primary, detail, primaryTone = "default", children }) {
    const toneCls =
        primaryTone === "rose"    ? "text-rose-600"   :
        primaryTone === "amber"   ? "text-amber-700"  :
        primaryTone === "primary" ? "text-primary"    :
        primaryTone === "muted"   ? "text-muted-foreground" :
        "text-foreground";
    return (
        <div className="rounded-lg border border-border bg-card p-3 flex flex-col">
            <div className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                {icon} {eyebrow}
            </div>
            <div className={`mt-1.5 text-[22px] font-display font-extrabold leading-none tabular-nums tracking-tight ${toneCls}`}>
                {primary}
            </div>
            {detail && <div className="text-[10.5px] text-muted-foreground mt-1">{detail}</div>}
            {children}
        </div>
    );
}


/* iter98.83 — Inline metadata pill. Small footprint, icon + label
 * (uppercase eyebrow) + value. Used in the metadata strip row. */
function MetaPill({ icon, label, value }) {
    return (
        <div className="rounded-md border border-border bg-secondary/30 px-2.5 py-1.5">
            <div className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                {icon} {label}
            </div>
            <div className="text-[12px] font-medium text-foreground truncate mt-0.5" title={typeof value === "string" ? value : undefined}>
                {value}
            </div>
        </div>
    );
}


function WearMiniChart({ series }) {
    const max = 24;
    return (
        <div className="flex items-end gap-0.5 h-8 my-2" data-testid="wear-mini-chart">
            {series.map((v, i) => {
                const h = Math.max(2, Math.round((v / max) * 32));
                const c = v === 0 ? "bg-border" : v < 16 ? "bg-rose-400" : v < 20 ? "bg-amber-400" : "bg-primary";
                return <div key={i} className={`flex-1 rounded-t-[2px] ${c}`} style={{ height: `${h}px` }} />;
            })}
        </div>
    );
}
